import { FC, useEffect, useRef, useState } from 'react';

import { Link } from '@inertiajs/react';
import { ArrowRight } from 'lucide-react';

import { Card, CardContent } from '../ui/Card';
import { Image } from '../ui/Image';

interface SelectorCardItem {
    id: number | string;
    name: string;
    image: string;
    href: string;
    slug?: string;
}

interface Props {
    readonly items: SelectorCardItem[];
    readonly activeItemSlug?: string | null;
    readonly testIdPrefix?: string;
    readonly variant?: 'default' | 'compact';
}

export const SelectorCardCarousel: FC<Props> = ({
    items,
    activeItemSlug,
    testIdPrefix,
    variant = 'default',
}) => {
    const sliderRef = useRef<HTMLDivElement>(null);
    const shouldScrollOnDesktop = items.length > 8;
    const hasSingleItem = items.length === 1;
    const isCompact = variant === 'compact';

    const [canScrollRight, setCanScrollRight] = useState(false);
    const [hasScrolled, setHasScrolled] = useState(false);

    const checkScroll = () => {
        const container = sliderRef.current;
        if (!container) return;

        const { scrollLeft, scrollWidth, clientWidth } = container;
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);

        if (scrollLeft > 0) {
            setHasScrolled(true);
        }
    };

    useEffect(() => {
        checkScroll();
        window.addEventListener('resize', checkScroll);

        return () => window.removeEventListener('resize', checkScroll);
    }, [items]);

    return (
        <div className="relative">
            <div
                className={`pointer-events-none absolute top-0 right-1 bottom-0 z-20 flex items-center justify-center transition-opacity duration-500 ${
                    canScrollRight && !hasScrolled ? 'opacity-100' : 'opacity-0'
                }`}
            >
                <div className="flex animate-pulse items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground shadow-md backdrop-blur-sm">
                    <span>Гортайте</span>
                    <ArrowRight className="h-3 w-3" />
                </div>
            </div>

            <div
                ref={sliderRef}
                onScroll={checkScroll}
                data-testid={
                    testIdPrefix ? `${testIdPrefix}-scroller` : undefined
                }
                className={`scrollbar flex snap-x snap-mandatory overflow-x-auto scrollbar-thumb-primary scrollbar-thumb-rounded-full scrollbar-track-accent scrollbar-track-rounded-full sm:gap-2 ${
                    shouldScrollOnDesktop ? '' : 'sm:overflow-x-visible'
                }`}
            >
                {items.map((item) => {
                    const isActive = activeItemSlug === item.slug;

                    return (
                        <div
                            key={item.id}
                            className={`w-1/4 flex-shrink-0 sm:min-w-[calc(12.5%-0.45rem)] ${
                                hasSingleItem
                                    ? 'sm:max-w-64 sm:flex-none'
                                    : 'sm:flex-1'
                            }`}
                        >
                            <Link
                                href={item.href}
                                className="group flex h-full"
                                aria-current={isActive ? 'page' : undefined}
                                data-testid={
                                    testIdPrefix
                                        ? `${testIdPrefix}-${item.slug}`
                                        : undefined
                                }
                            >
                                <Card
                                    className={`mb-2 flex w-full flex-col overflow-hidden rounded-2xl border-0 shadow-none transition-all dark:border-0 dark:bg-transparent sm:dark:border sm:dark:bg-card ${
                                        hasSingleItem ? 'sm:max-h-64' : ''
                                    } ${
                                        isActive
                                            ? 'sm:border-primary/50 sm:bg-accent/30 sm:shadow-md sm:ring-1 sm:ring-primary/10'
                                            : 'sm:border sm:shadow-sm sm:hover:shadow-md'
                                    }`}
                                >
                                    <div
                                        className={`relative w-full flex-shrink-0 ${
                                            isCompact
                                                ? 'aspect-video min-h-0 overflow-hidden sm:aspect-[2/1]'
                                                : 'aspect-video'
                                        }`}
                                    >
                                        <Image
                                            src={item.image}
                                            alt={item.name}
                                            className={`h-full w-full scale-120 object-contain transition-transform group-hover:scale-105 sm:scale-none ${
                                                isCompact
                                                    ? 'sm:pt-4 2xl:pt-5'
                                                    : ''
                                            }`}
                                        />
                                    </div>
                                    <CardContent
                                        className={`flex items-center justify-center px-0 text-center ${
                                            isCompact
                                                ? 'shrink-0 py-2.5 sm:px-3 sm:py-3'
                                                : 'flex-1 py-3 sm:p-4'
                                        }`}
                                    >
                                        <h3 className="text-xs font-medium md:text-base md:font-semibold">
                                            {item.name}
                                        </h3>
                                    </CardContent>
                                </Card>
                            </Link>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
